import { ConnectionKind } from "@redotech/redo-integrations-core/connection-type/connection-kind";
import * as z from "zod";
import {
  HttpMetadata,
  HttpSecret,
  httpConnectionType,
  httpSecretMismatch,
  httpUpdateError,
} from "./http.connection-type";

export const connectionTypes = {
  [ConnectionKind.Http]: httpConnectionType,
} as const satisfies { [K in ConnectionKind]: { kind: K } };

type ConnectionTypes = typeof connectionTypes;

export const ConnectionName = z.string().trim().min(1).max(100);

/** Metadata tagged with its type, so narrowing `type` narrows `metadata`. */
type TypedConnectionMetadata = {
  [K in ConnectionKind]: {
    type: K;
    metadata: z.output<ConnectionTypes[K]["metadata"]>;
  };
}[ConnectionKind];

/** A secret tagged with its type, so narrowing `type` narrows `secret`. */
export type TypedConnectionSecret = {
  [K in ConnectionKind]: {
    type: K;
    secret: z.output<ConnectionTypes[K]["secret"]>;
  };
}[ConnectionKind];

type ParseResult<T> =
  | { success: true; data: T }
  | { success: false; error: z.ZodError };

function tag<T, R>(
  result: z.ZodSafeParseResult<T>,
  wrap: (data: T) => R,
): ParseResult<R> {
  return result.success
    ? { success: true, data: wrap(result.data) }
    : { success: false, error: result.error };
}

export function parseConnectionMetadata(
  type: ConnectionKind,
  value: unknown,
): ParseResult<TypedConnectionMetadata> {
  switch (type) {
    case ConnectionKind.Http:
      return tag(HttpMetadata.safeParse(value), (metadata) => ({
        type,
        metadata,
      }));
  }
}

export function parseConnectionSecret(
  type: ConnectionKind,
  value: unknown,
): ParseResult<TypedConnectionSecret> {
  switch (type) {
    case ConnectionKind.Http:
      return tag(HttpSecret.safeParse(value), (secret) => ({ type, secret }));
  }
}

/**
 * Why a secret that passes its type's schema can't be used with the
 * connection's metadata (such as an http secret for another auth scheme), or null.
 */
export function connectionSecretMismatch(
  connection: TypedConnectionMetadata,
  secret: TypedConnectionSecret,
): string | null {
  if (connection.type !== secret.type) {
    return `A ${secret.type} secret can't be used with a ${connection.type} connection`;
  }
  switch (connection.type) {
    case ConnectionKind.Http:
      return secret.type === ConnectionKind.Http
        ? httpSecretMismatch(connection.metadata, secret.secret)
        : null;
  }
}

/** Why `current` metadata can't be replaced by `next`, or null. */
export function connectionUpdateError(
  current: TypedConnectionMetadata,
  next: TypedConnectionMetadata,
): string | null {
  if (current.type !== next.type) {
    return "A connection's type can't change";
  }
  switch (current.type) {
    case ConnectionKind.Http:
      return next.type === ConnectionKind.Http
        ? httpUpdateError(current.metadata, next.metadata)
        : null;
  }
}
